import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { NetworkArea } from '../../interfaces/services/areas';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleLineStringPopupMouseEnter,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import {
    addEmptyMilesAreasCenterLayer,
    addEmptyMilesAreasLaneCenterLayer,
    addEmptyMilesAreasLaneLineLayer,
} from '../../utils/maps/layers/emptyMilesAreas';
import { EmptyMilesAreasMapSourceProperties, addEmptyMilesAreasSource } from '../../utils/maps/sources/emptyMilesAreas';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'emptyMilesMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface EmptyMilesMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Areas to add to the map */
    emptyMilesAreas: NetworkArea[];
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface EmptyMilesMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class EmptyMilesMap extends React.Component<EmptyMilesMapProps, EmptyMilesMapState> {

    constructor(props: EmptyMilesMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeEmptyMilesAreasEventHandlers();
            this.addEmptyMilesAreasToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: EmptyMilesMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add empty miles areas to map if props have updated
        if (prevProps.emptyMilesAreas !== this.props.emptyMilesAreas) {
            this.removeEmptyMilesAreasEventHandlers();
            this.addEmptyMilesAreasToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeEmptyMilesAreasEventHandlers();
    }

    addEmptyMilesAreasToMap = (): void => {
        if (map) {
            // add map source
            const emptyMilesAreasSource = addEmptyMilesAreasSource(map, this.props.emptyMilesAreas || [], this.props.theme);

            // add area lane lines
            addEmptyMilesAreasLaneLineLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.emptyMilesAreasLaneLineLayer, (e: any): void => {
                handleLineStringPopupMouseEnter(map, MapLayers.emptyMilesAreasLaneLineLayer, e, trimbleMapsPopup);
            });
            map.on('mouseleave', MapLayers.emptyMilesAreasLaneLineLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            // add area lane centers
            addEmptyMilesAreasLaneCenterLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: true,
            });

            // add area centers
            addEmptyMilesAreasCenterLayer({
                map,
                mapSource: MapSources.emptyMilesAreasSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.emptyMilesAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.emptyMilesAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const { data } = emptyMilesAreasSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Point, EmptyMilesAreasMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeEmptyMilesAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.emptyMilesAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.emptyMilesAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.emptyMilesAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }

            if (map.getLayer(MapLayers.emptyMilesAreasLaneLineLayer) !== undefined) {
                map.off('mouseenter', MapLayers.emptyMilesAreasLaneLineLayer, (e): void => {
                    handleLineStringPopupMouseEnter(map, MapLayers.emptyMilesAreasLaneLineLayer, e, trimbleMapsPopup);
                });
                map.off('mouseleave', MapLayers.emptyMilesAreasLaneLineLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='emptyMilesMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default EmptyMilesMap;
